import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "./Button";
import styles from "./ErrorBoundary.module.css";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback component to render on error */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 * Displays a fallback UI when an error occurs.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * Erreurs de chargement/évaluation de chunk lazy : un reload récupère le cas
   * (nouveau HTML + chunks cohérents). Patterns couverts :
   * - webpack : ChunkLoadError / « Loading chunk »
   * - Vite/Chrome : « Failed to fetch dynamically imported module »,
   *   « Unable to preload CSS »
   * - Firefox : « error loading dynamically imported module »
   * - Safari : « Importing a module script failed » et le flake React.lazy
   *   « _result.default » (module résolu vide au retour bfcache, KENOMETRIC-3M)
   */
  private static isChunkError(error: Error): boolean {
    if (error.name === "ChunkLoadError") return true;
    const msg = error.message ?? "";
    return (
      msg.includes("Loading chunk") ||
      msg.includes("dynamically imported module") ||
      msg.includes("Unable to preload CSS") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("_result.default")
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Chunk manquant/corrompu (deploy entre-temps, flake Safari…) → reload once
    if (ErrorBoundary.isChunkError(error)) {
      const key = "chunk_reload_" + window.location.pathname;
      if (!(() => { try { return sessionStorage.getItem(key); } catch { return null; } })()) {
        try { sessionStorage.setItem(key, "1"); } catch { /* sessionStorage blocked */ }
        window.location.reload();
        return;
      }
    }

    console.error("ErrorBoundary caught an error:", error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconWrapper}>
              <AlertTriangle size={32} />
            </div>
            <h2 className={styles.title}>Une erreur est survenue</h2>
            <p className={styles.description}>
              Nous sommes désolés, quelque chose s'est mal passé.
            </p>
            {this.state.error && (
              <details className={styles.details}>
                <summary>Détails techniques</summary>
                <pre className={styles.errorMessage}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <Button onClick={this.handleReset} variant="secondary" className={styles.retryButton}>
              <RefreshCcw size={16} />
              Réessayer
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
